// consentOutbox.js -- at-least-once delivery of consent rows (spec §11). Single job: persist rows
// BEFORE sending, send one user's entries single-flight (including any enqueued mid-flight), remove
// each by id on success, park terminal rejects (one exception), drop ST021-blocked entries (the gate
// refetches), alert once after 7 days, warn on transient failures.
import { randomUUID } from '../../lib/platform';
import * as Sentry from '../../lib/errorReporting';
import { readDevice, updateDevice } from './deviceRecords';
import { insertRows } from './accountRecords';

const QUEUE_ALERT_MS = 7 * 24 * 60 * 60 * 1000;
const inflight = new Map();

export function enqueueRows(userId, rows, now) {
  const entry = { id: randomUUID(), userId, rows, queuedAt: new Date(now).toISOString(), parked: false, alerted: false };
  updateDevice((d) => ({ queue: [...d.queue, entry] }));
  return entry;
}
// The session the send runs as. A sign-out or user switch mid-flush would send A's rows as anon or as B
// and get 42501, which would park valid rows: stop instead and resend later. Its access token is pinned
// on the insert (#1438 F2), so the session cannot change between this check and the request.
async function sendingSession(client) {
  try {
    const { data } = await client.auth.getSession();
    const s = data?.session;
    return { userId: s?.user?.id || null, accessToken: s?.access_token || null };
  } catch {
    Sentry.captureMessage('onboarding: session read failed before a consent send', { level: 'warning', tags: { function: 'flushOutbox', stage: 'session' } });
    return { userId: null, accessToken: null };
  }
}
const patchEntry = (id, patch) => updateDevice((d) => ({ queue: d.queue.map((q) => (q.id === id ? { ...q, ...patch } : q)) }));
const removeEntry = (id) => updateDevice((d) => ({ queue: d.queue.filter((q) => q.id !== id) }));

// Resolves { blocked, parked }. blocked: the server's 24h under-21 guard rejected an entry (the caller
// refetches; the under_21 row resolves the gate to `blocked`). parked: a terminal reject; the caller
// refetches so rows merged locally stop satisfying the gate and the user is asked again. onBlocked(userId),
// if given, is called at the first ST021 of the pass, before any later send in it answers (#1464: the gate
// records the block at once; a hung later send must not hold it back). A joined flush uses the first
// caller's onBlocked.
export function flushOutbox(client, userId, { now = Date.now(), onBlocked } = {}) {
  if (inflight.has(userId)) return inflight.get(userId);
  const run = (async () => {
    let blocked = false;
    let parked = false;
    // Re-read each turn so entries enqueued mid-flight are sent in this pass; each is tried once.
    const tried = new Set();
    for (;;) {
      const entry = readDevice().queue.find((q) => q.userId === userId && !q.parked && !tried.has(q.id));
      if (!entry) break;
      const session = await sendingSession(client);
      if (session.userId !== userId || !session.accessToken) break;
      tried.add(entry.id);
      const res = await insertRows(client, entry.rows, { accessToken: session.accessToken });
      if (res.ok) { removeEntry(entry.id); continue; }
      if (res.blocked) {
        // Expected outcome of the server block, not a defect: drop the entry, never re-ask during the block.
        // No `function` tag on purpose: errorReporting forwards only function-tagged messages to PostHog,
        // and the blocked path sends nothing to analytics (spec D9). Sentry only.
        removeEntry(entry.id);
        if (!blocked && typeof onBlocked === 'function') {
          try {
            onBlocked(userId);
          } catch {
            // The pass goes on and still resolves { blocked: true }; the caller records it from that (#1464).
            Sentry.captureMessage('onboarding: onBlocked callback failed', { level: 'warning', tags: { function: 'flushOutbox', stage: 'onBlocked' } });
          }
        }
        blocked = true;
        Sentry.captureMessage('onboarding: consent insert blocked by the 24h age guard (ST021)', { level: 'info', tags: { area: 'onboarding', stage: 'blocked' } });
        continue;
      }
      if (res.terminal) {
        patchEntry(entry.id, { parked: true });
        parked = true;
        Sentry.captureException(new Error('onboarding: consent record rejected (parked)'), { tags: { function: 'flushOutbox', stage: 'parked', code: res.code } });
        continue;
      }
      Sentry.captureMessage('onboarding: consent insert failed (kept in outbox)', { level: 'warning', tags: { function: 'flushOutbox', stage: 'transient' } });
      if (!entry.alerted && now - Date.parse(entry.queuedAt) > QUEUE_ALERT_MS) {
        patchEntry(entry.id, { alerted: true });
        Sentry.captureException(new Error('onboarding: consent record unsent > 7 days'), { tags: { function: 'flushOutbox', stage: 'stale' } });
      }
    }
    return { blocked, parked };
  })();
  // Released after settle, never inside the body: with nothing pending the body completes
  // synchronously, so an in-body delete would run before the set and wedge the slot forever.
  inflight.set(userId, run);
  run.finally(() => { if (inflight.get(userId) === run) inflight.delete(userId); }).catch(() => {});
  return run;
}
export function resetOutboxForTests() { inflight.clear(); }
